import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Brain, Heart, Wind, Eye, Scan, Utensils, Network, Target, TrendingUp, Users, Award, Globe, Lightbulb, DollarSign, Shield, Zap, Calendar, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import MorphingGradient from '../components/MorphingGradient';
import AmbientBackground from '../components/AmbientBackground';
import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';

const BusinessPlan = () => {
  const { t, language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const slides = [
    {
      id: 'cover',
      title: {
        en: 'YamAI B.V.',
        nl: 'YamAI B.V.',
        ar: 'YamAI B.V.'
      },
      subtitle: {
        en: 'Business & Innovation Plan',
        nl: 'Bedrijfs- en Innovatieplan',
        ar: 'خطة الأعمال والابتكار'
      },
      description: {
        en: 'Explainable AI for Next-Generation Medical Diagnostics',
        nl: 'Verklaarbare AI voor Medische Diagnostiek van de Volgende Generatie',
        ar: 'ذكاء اصطناعي قابل للتفسير للتشخيص الطبي من الجيل القادم'
      },
      icon: Brain,
      gradient: 'from-[#7C3AED] to-[#14B8A6]'
    },
    {
      id: 'vision',
      title: {
        en: 'Vision & Mission',
        nl: 'Visie & Missie',
        ar: 'الرؤية والرسالة'
      },
      content: [
        {
          label: { en: 'Vision', nl: 'Visie', ar: 'الرؤية' },
          text: {
            en: 'To become Europe\'s leading provider of explainable AI-powered diagnostic solutions, transforming healthcare through transparent, reliable, and organ-specific medical intelligence.',
            nl: 'De leidende aanbieder worden in Europa van verklaarbare AI-gedreven diagnostische oplossingen, waarbij de gezondheidszorg wordt getransformeerd door transparante, betrouwbare en orgaanspecifieke medische intelligentie.',
            ar: 'أن نصبح المزود الرائد في أوروبا لحلول التشخيص المدعومة بالذكاء الاصطناعي القابل للتفسير، لتحويل الرعاية الصحية من خلال الذكاء الطبي الشفاف والموثوق والمتخصص بالأعضاء.'
          },
          icon: Target
        },
        {
          label: { en: 'Mission', nl: 'Missie', ar: 'الرسالة' },
          text: {
            en: 'Develop clinically validated, explainable AI models that empower healthcare professionals with accurate, transparent diagnostic insights while maintaining the highest ethical and regulatory standards.',
            nl: 'Klinisch gevalideerde, verklaarbare AI-modellen ontwikkelen die zorgprofessionals in staat stellen nauwkeurige, transparante diagnostische inzichten te verkrijgen met behoud van de hoogste ethische en regelgevende normen.',
            ar: 'تطوير نماذج ذكاء اصطناعي قابلة للتفسير ومعتمدة سريرياً تمكّن المتخصصين في الرعاية الصحية من الحصول على رؤى تشخيصية دقيقة وشفافة مع الحفاظ على أعلى المعايير الأخلاقية والتنظيمية.'
          },
          icon: Lightbulb
        }
      ],
      icon: Target,
      gradient: 'from-[#14B8A6] to-[#7C3AED]'
    },
    {
      id: 'problem',
      title: {
        en: 'The Problem We Solve',
        nl: 'Het Probleem Dat Wij Oplossen',
        ar: 'المشكلة التي نحلها'
      },
      content: [
        {
          stat: '60%',
          label: {
            en: 'Diagnostic Errors',
            nl: 'Diagnostische Fouten',
            ar: 'أخطاء التشخيص'
          },
          text: {
            en: 'AI diagnostic systems lack transparency, making clinical adoption difficult',
            nl: 'AI-diagnostische systemen missen transparantie, wat klinische adoptie moeilijk maakt',
            ar: 'تفتقر أنظمة التشخيص بالذكاء الاصطناعي إلى الشفافية مما يجعل التبني السريري صعباً'
          }
        },
        {
          stat: '€280B',
          label: {
            en: 'Annual Healthcare Costs',
            nl: 'Jaarlijkse Zorgkosten',
            ar: 'تكاليف الرعاية الصحية السنوية'
          },
          text: {
            en: 'Late detection and misdiagnosis cost EU healthcare systems billions annually',
            nl: 'Late detectie en verkeerde diagnose kosten EU-zorgsystemen jaarlijks miljarden',
            ar: 'يكلف الاكتشاف المتأخر والتشخيص الخاطئ أنظمة الرعاية الصحية في الاتحاد الأوروبي مليارات سنوياً'
          }
        },
        {
          stat: '40%',
          label: {
            en: 'Physician Burnout',
            nl: 'Uitputting van Artsen',
            ar: 'إرهاق الأطباء'
          },
          text: {
            en: 'Overwhelming data and manual analysis contribute to clinician fatigue',
            nl: 'Overweldigende data en handmatige analyse dragen bij aan vermoeidheid van clinici',
            ar: 'تساهم البيانات الهائلة والتحليل اليدوي في إرهاق الأطباء'
          }
        }
      ],
      icon: Shield,
      gradient: 'from-[#EC4899] to-[#8B5CF6]'
    },
    {
      id: 'solution',
      title: {
        en: 'Our Solution',
        nl: 'Onze Oplossing',
        ar: 'حلنا'
      },
      subtitle: {
        en: 'Seven Specialized AI Systems',
        nl: 'Zeven Gespecialiseerde AI-Systemen',
        ar: 'سبعة أنظمة ذكاء اصطناعي متخصصة'
      },
      products: [
        {
          name: 'Yamai Cardio',
          icon: Heart,
          color: '#14B8A6',
          description: {
            en: 'Echo + ECG fusion for early cardiac abnormality detection',
            nl: 'Echo + ECG-fusie voor vroege detectie van hartafwijkingen',
            ar: 'دمج الإيكو وتخطيط القلب للكشف المبكر عن اضطرابات القلب'
          }
        },
        {
          name: 'Yamai Neuro',
          icon: Brain,
          color: '#7C3AED',
          description: {
            en: 'Neural pattern analysis for neurological condition detection',
            nl: 'Neurale patroonanalyse voor detectie van neurologische aandoeningen',
            ar: 'تحليل الأنماط العصبية للكشف عن الحالات العصبية'
          }
        },
        {
          name: 'Yamai Derma',
          icon: Scan,
          color: '#EC4899',
          description: {
            en: 'Computer vision for dermatological tissue analysis',
            nl: 'Computer vision voor dermatologische weefselanalyse',
            ar: 'رؤية حاسوبية لتحليل الأنسجة الجلدية'
          }
        },
        {
          name: 'Yamai Pulmo',
          icon: Wind,
          color: '#3B82F6',
          description: {
            en: 'Respiratory function analysis and lung health assessment',
            nl: 'Analyse van ademhalingsfunctie en longgezondheidsbeoordeling',
            ar: 'تحليل وظائف الجهاز التنفسي وتقييم صحة الرئة'
          }
        },
        {
          name: 'Yamai Vision',
          icon: Eye,
          color: '#8B5CF6',
          description: {
            en: 'Retinal pattern detection for eye disease diagnosis',
            nl: 'Retinale patroondetectie voor oogziektediagnose',
            ar: 'اكتشاف أنماط الشبكية لتشخيص أمراض العين'
          }
        },
        {
          name: 'Yamai NutriScan',
          icon: Utensils,
          color: '#10B981',
          description: {
            en: 'Metabolic signal analysis for personalized nutrition',
            nl: 'Metabole signaalanalyse voor gepersonaliseerde voeding',
            ar: 'تحليل الإشارات الأيضية للتغذية الشخصية'
          }
        }
      ],
      icon: Zap,
      gradient: 'from-[#10B981] to-[#06B6D4]'
    },
    {
      id: 'market',
      title: {
        en: 'Market Opportunity',
        nl: 'Marktkansen',
        ar: 'فرصة السوق'
      },
      markets: [
        {
          label: { en: 'EU Medical AI Market', nl: 'EU Medische AI-Markt', ar: 'سوق الذكاء الاصطناعي الطبي في الاتحاد الأوروبي' },
          value: '€12.5B',
          growth: '+42% CAGR',
          year: '2025-2030',
          icon: TrendingUp
        },
        {
          label: { en: 'Cardiology AI Segment', nl: 'Cardiologie AI-Segment', ar: 'قطاع ذكاء أمراض القلب' },
          value: '€3.2B',
          growth: '+38% CAGR',
          year: '2025-2030',
          icon: Heart
        },
        {
          label: { en: 'Target Hospitals (EU)', nl: 'Doelziekenhuizen (EU)', ar: 'المستشفيات المستهدفة (الاتحاد الأوروبي)' },
          value: '5,000+',
          growth: 'Initial TAM',
          year: '2025',
          icon: Globe
        }
      ],
      icon: TrendingUp,
      gradient: 'from-[#F59E0B] to-[#EF4444]'
    },
    {
      id: 'competitive',
      title: {
        en: 'Competitive Advantages',
        nl: 'Concurrentievoordelen',
        ar: 'المزايا التنافسية'
      },
      advantages: [
        {
          title: { en: 'Explainable AI', nl: 'Verklaarbare AI', ar: 'ذكاء اصطناعي قابل للتفسير' },
          description: {
            en: 'Transparent decision-making process that clinicians can understand and trust',
            nl: 'Transparant besluitvormingsproces dat clinici kunnen begrijpen en vertrouwen',
            ar: 'عملية اتخاذ قرارات شفافة يمكن للأطباء فهمها والوثوق بها'
          },
          icon: Shield
        },
        {
          title: { en: 'Multi-Modal Integration', nl: 'Multi-Modale Integratie', ar: 'التكامل متعدد الوسائط' },
          description: {
            en: 'Combines multiple data sources (imaging, signals, lab data) for holistic analysis',
            nl: 'Combineert meerdere gegevensbronnen (beeldvorming, signalen, laboratoriumgegevens) voor holistische analyse',
            ar: 'يجمع مصادر بيانات متعددة (التصوير، الإشارات، بيانات المختبر) للتحليل الشامل'
          },
          icon: Network
        },
        {
          title: { en: 'Clinical Validation', nl: 'Klinische Validatie', ar: 'التحقق السريري' },
          description: {
            en: 'Rigorous testing with medical professionals and real-world clinical data',
            nl: 'Rigoureuze tests met medische professionals en real-world klinische gegevens',
            ar: 'اختبارات صارمة مع المتخصصين الطبيين والبيانات السريرية الواقعية'
          },
          icon: Award
        },
        {
          title: { en: 'GDPR Compliant', nl: 'GDPR-conform', ar: 'متوافق مع GDPR' },
          description: {
            en: 'Built with privacy-first architecture and full EU data protection compliance',
            nl: 'Gebouwd met privacy-first architectuur en volledige EU-gegevensbescherming',
            ar: 'مبني بهندسة معمارية تركز على الخصوصية ومتوافق تماماً مع حماية البيانات في الاتحاد الأوروبي'
          },
          icon: Shield
        }
      ],
      icon: Award,
      gradient: 'from-[#8B5CF6] to-[#EC4899]'
    },
    {
      id: 'roadmap',
      title: {
        en: 'Development Roadmap',
        nl: 'Ontwikkelingsroutekaart',
        ar: 'خارطة طريق التطوير'
      },
      phases: [
        {
          period: 'Q4 2025 - Q2 2026',
          title: { en: 'Cardio Prototype', nl: 'Cardio Prototype', ar: 'نموذج القلب' },
          tasks: [
            { en: 'Complete Echo + ECG AI model development', nl: 'Voltooi Echo + ECG AI-modelontwikkeling', ar: 'إكمال تطوير نموذج الذكاء الاصطناعي للإيكو وتخطيط القلب' },
            { en: 'Initial laboratory testing', nl: 'Initiële laboratoriumtests', ar: 'الاختبارات المعملية الأولية' },
            { en: 'Build explainability module', nl: 'Bouw verklaarbaar heidsmodule', ar: 'بناء وحدة التفسير' }
          ],
          color: '#14B8A6'
        },
        {
          period: 'Q3 2026 - Q2 2027',
          title: { en: 'Clinical Validation', nl: 'Klinische Validatie', ar: 'التحقق السريري' },
          tasks: [
            { en: 'Partner with NL/BE hospitals', nl: 'Partner met NL/BE-ziekenhuizen', ar: 'الشراكة مع مستشفيات هولندا وبلجيكا' },
            { en: 'Conduct clinical trials (500+ patients)', nl: 'Voer klinische proeven uit (500+ patiënten)', ar: 'إجراء التجارب السريرية (500+ مريض)' },
            { en: 'Collect real-world performance data', nl: 'Verzamel real-world prestatiegegevens', ar: 'جمع بيانات الأداء الواقعي' }
          ],
          color: '#7C3AED'
        },
        {
          period: 'Q3 2027 - Q4 2028',
          title: { en: 'MDR Certification', nl: 'MDR-Certificering', ar: 'شهادة MDR' },
          tasks: [
            { en: 'Submit MDR/CE marking dossier', nl: 'Dien MDR/CE-markering dossier in', ar: 'تقديم ملف شهادة MDR/CE' },
            { en: 'Undergo notified body assessment', nl: 'Doorloop beoordelingsprocedure', ar: 'الخضوع لتقييم الجهة المُبلغة' },
            { en: 'Achieve Class IIb medical device status', nl: 'Behaal Klasse IIb medische apparaatstatus', ar: 'الحصول على حالة الجهاز الطبي من الفئة IIb' }
          ],
          color: '#EC4899'
        },
        {
          period: '2029 - 2030',
          title: { en: 'Market Launch', nl: 'Marktintroductie', ar: 'إطلاق السوق' },
          tasks: [
            { en: 'Commercial rollout in BeNeLux', nl: 'Commerciële uitrol in BeNeLux', ar: 'الطرح التجاري في دول البنلوكس' },
            { en: 'Expand to Germany, France', nl: 'Uitbreiden naar Duitsland, Frankrijk', ar: 'التوسع إلى ألمانيا وفرنسا' },
            { en: 'Begin Neuro/Derma development', nl: 'Begin Neuro/Derma-ontwikkeling', ar: 'بدء تطوير Neuro/Derma' }
          ],
          color: '#10B981'
        },
        {
          period: '2031 - 2035',
          title: { en: 'HealthNet Integration', nl: 'HealthNet Integratie', ar: 'تكامل HealthNet' },
          tasks: [
            { en: 'Complete all 6 organ systems', nl: 'Voltooi alle 6 orgaansystemen', ar: 'إكمال جميع أنظمة الأعضاء الستة' },
            { en: 'Launch unified HealthNet platform', nl: 'Lanceer verenigd HealthNet-platform', ar: 'إطلاق منصة HealthNet الموحدة' },
            { en: 'Pan-European expansion', nl: 'Pan-Europese expansie', ar: 'التوسع الأوروبي الشامل' }
          ],
          color: '#F59E0B'
        }
      ],
      icon: Calendar,
      gradient: 'from-[#06B6D4] to-[#3B82F6]'
    },
    {
      id: 'business-model',
      title: {
        en: 'Business Model',
        nl: 'Bedrijfsmodel',
        ar: 'نموذج العمل'
      },
      models: [
        {
          type: { en: 'SaaS Licensing', nl: 'SaaS-Licenties', ar: 'ترخيص SaaS' },
          description: {
            en: 'Annual subscription per hospital/diagnostic center',
            nl: 'Jaarabonnement per ziekenhuis/diagnostisch centrum',
            ar: 'اشتراك سنوي لكل مستشفى/مركز تشخيصي'
          },
          pricing: '€50K - €150K/year',
          icon: DollarSign
        },
        {
          type: { en: 'Per-Analysis Fee', nl: 'Kosten per Analyse', ar: 'رسوم لكل تحليل' },
          description: {
            en: 'Pay-per-use model for smaller clinics',
            nl: 'Pay-per-use model voor kleinere klinieken',
            ar: 'نموذج الدفع لكل استخدام للعيادات الصغيرة'
          },
          pricing: '€15 - €50/scan',
          icon: Zap
        },
        {
          type: { en: 'Enterprise Integration', nl: 'Enterprise Integratie', ar: 'تكامل المؤسسات' },
          description: {
            en: 'Custom deployment for hospital networks',
            nl: 'Aangepaste implementatie voor ziekenhuisnetwerken',
            ar: 'نشر مخصص لشبكات المستشفيات'
          },
          pricing: '€500K+/year',
          icon: Network
        }
      ],
      icon: DollarSign,
      gradient: 'from-[#10B981] to-[#14B8A6]'
    },
    {
      id: 'financials',
      title: {
        en: 'Financial Projections',
        nl: 'Financiële Projecties',
        ar: 'التوقعات المالية'
      },
      projections: [
        {
          year: '2026',
          revenue: '€0.5M',
          customers: '5 Pilot Sites',
          status: { en: 'Validation Phase', nl: 'Validatiefase', ar: 'مرحلة التحقق' }
        },
        {
          year: '2028',
          revenue: '€3.2M',
          customers: '25 Hospitals',
          status: { en: 'Market Entry', nl: 'Marktintrede', ar: 'دخول السوق' }
        },
        {
          year: '2030',
          revenue: '€15M',
          customers: '120 Hospitals',
          status: { en: 'Growth Phase', nl: 'Groeifase', ar: 'مرحلة النمو' }
        },
        {
          year: '2033',
          revenue: '€50M+',
          customers: '400+ Hospitals',
          status: { en: 'Market Leader', nl: 'Marktleider', ar: 'قائد السوق' }
        }
      ],
      icon: TrendingUp,
      gradient: 'from-[#EF4444] to-[#F59E0B]'
    },
    {
      id: 'team',
      title: {
        en: 'Leadership Team',
        nl: 'Leiderschapsteam',
        ar: 'فريق القيادة'
      },
      subtitle: {
        en: 'Expertise in AI, Medicine, and Healthcare Innovation',
        nl: 'Expertise in AI, Geneeskunde en Gezondheidszorginnovatie',
        ar: 'خبرة في الذكاء الاصطناعي والطب وابتكار الرعاية الصحية'
      },
      description: {
        en: 'Our multidisciplinary team combines deep technical expertise in AI and machine learning with extensive clinical knowledge and healthcare industry experience.',
        nl: 'Ons multidisciplinaire team combineert diepgaande technische expertise in AI en machine learning met uitgebreide klinische kennis en ervaring in de gezondheidszorgsector.',
        ar: 'يجمع فريقنا متعدد التخصصات بين الخبرة التقنية العميقة في الذكاء الاصطناعي والتعلم الآلي مع المعرفة السريرية الواسعة والخبرة في صناعة الرعاية الصحية.'
      },
      icon: Users,
      gradient: 'from-[#7C3AED] to-[#EC4899]'
    },
    {
      id: 'call-to-action',
      title: {
        en: 'Join Us in Transforming Healthcare',
        nl: 'Sluit Je Aan Bij Ons in Het Transformeren van de Gezondheidszorg',
        ar: 'انضم إلينا في تحويل الرعاية الصحية'
      },
      subtitle: {
        en: 'Investment Opportunity',
        nl: 'Investeringsmogelijkheid',
        ar: 'فرصة استثمارية'
      },
      content: {
        seeking: {
          en: 'Seeking: €2.5M Seed Round',
          nl: 'Zoeken naar: €2,5M Seed-ronde',
          ar: 'نبحث عن: 2.5 مليون يورو جولة تمويل أولية'
        },
        use: {
          en: 'Use of Funds',
          nl: 'Gebruik van Fondsen',
          ar: 'استخدام الأموال'
        },
        breakdown: [
          { label: { en: 'R&D & Clinical Trials', nl: 'R&D & Klinische Proeven', ar: 'البحث والتطوير والتجارب السريرية' }, value: '60%' },
          { label: { en: 'Regulatory & Certification', nl: 'Regelgeving & Certificering', ar: 'التنظيم والشهادات' }, value: '20%' },
          { label: { en: 'Team Expansion', nl: 'Teamuitbreiding', ar: 'توسيع الفريق' }, value: '15%' },
          { label: { en: 'Operations', nl: 'Operaties', ar: 'العمليات' }, value: '5%' }
        ]
      },
      contact: {
        en: 'Contact us to learn more about this transformative opportunity',
        nl: 'Neem contact met ons op om meer te weten te komen over deze transformerende kans',
        ar: 'اتصل بنا لمعرفة المزيد عن هذه الفرصة التحويلية'
      },
      icon: Target,
      gradient: 'from-[#14B8A6] to-[#7C3AED]'
    }
  ];

  const totalSlides = slides.length;

  useEffect(() => {
    const handleWheel = (e) => {
      if (isScrolling) return;

      if (e.deltaY > 0 && currentSlide < totalSlides - 1) {
        // Scroll down
        setIsScrolling(true);
        setCurrentSlide(prev => prev + 1);
        setTimeout(() => setIsScrolling(false), 800);
      } else if (e.deltaY < 0 && currentSlide > 0) {
        // Scroll up
        setIsScrolling(true);
        setCurrentSlide(prev => prev - 1);
        setTimeout(() => setIsScrolling(false), 800);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [currentSlide, isScrolling, totalSlides]);

  const goToSlide = (index) => {
    if (!isScrolling) {
      setCurrentSlide(index);
    }
  };

  const renderSlideContent = (slide) => {
    const SlideIcon = slide.icon || Brain;

    switch (slide.id) {
      case 'cover':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 1, type: 'spring' }}
              className="mb-8"
            >
              {slide.icon && <SlideIcon size={120} className={`bg-gradient-to-br ${slide.gradient} bg-clip-text text-transparent`} style={{ stroke: 'url(#gradient)' }} />}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={`text-7xl font-bold mb-4 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-4xl font-semibold text-white mb-6"
            >
              {slide.subtitle[language]}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="text-xl text-[#8A90A0] max-w-3xl"
            >
              {slide.description[language]}
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-12 text-[#8A90A0]"
            >
              <p className="text-sm">Breda, Netherlands • 2025</p>
            </motion.div>
          </div>
        );

      case 'vision':
        return (
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-12">
              {slide.content.map((item, index) => {
                const ItemIcon = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 * (index + 1) }}
                    className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-8 border border-[#7C3AED]/30 hover:border-[#14B8A6]/50 transition-all duration-300"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${slide.gradient} flex items-center justify-center`}>
                        <ItemIcon size={32} className="text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-white">{item.label[language]}</h3>
                    </div>
                    <p className="text-lg text-[#8A90A0] leading-relaxed">{item.text[language]}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );

      case 'problem':
        return (
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-8">
              {slide.content.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 * (index + 1) }}
                  className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-8 border border-[#EC4899]/30 hover:border-[#8B5CF6]/50 transition-all duration-300 text-center"
                >
                  <div className={`text-6xl font-bold mb-4 bg-gradient-to-br ${slide.gradient} bg-clip-text text-transparent`}>
                    {item.stat}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{item.label[language]}</h3>
                  <p className="text-[#8A90A0] leading-relaxed">{item.text[language]}</p>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'solution':
        return (
          <div className="max-w-7xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-4 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-2xl text-[#8A90A0] text-center mb-12"
            >
              {slide.subtitle[language]}
            </motion.p>
            <div className="grid md:grid-cols-3 gap-6">
              {slide.products.map((product, index) => {
                const ProductIcon = product.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * (index + 1) }}
                    className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-2xl p-6 border border-[#1F2A3A] hover:border-opacity-50 transition-all duration-300 group"
                    style={{ borderColor: `${product.color}40` }}
                  >
                    <div
                      className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ background: `linear-gradient(135deg, ${product.color}33, ${product.color}66)` }}
                    >
                      <ProductIcon size={28} style={{ color: product.color }} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{product.name}</h3>
                    <p className="text-sm text-[#8A90A0] leading-relaxed">{product.description[language]}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );

      case 'market':
        return (
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-8">
              {slide.markets.map((market, index) => {
                const MarketIcon = market.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 * (index + 1) }}
                    className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-8 border border-[#F59E0B]/30 hover:border-[#EF4444]/50 transition-all duration-300"
                  >
                    <MarketIcon size={48} className="text-[#F59E0B] mb-6" />
                    <div className={`text-5xl font-bold mb-3 bg-gradient-to-br ${slide.gradient} bg-clip-text text-transparent`}>
                      {market.value}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{market.label[language]}</h3>
                    <div className="flex items-center gap-2 text-[#10B981] font-semibold">
                      <TrendingUp size={20} />
                      <span>{market.growth}</span>
                    </div>
                    <p className="text-[#8A90A0] mt-2">{market.year}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );

      case 'competitive':
        return (
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              {slide.advantages.map((advantage, index) => {
                const AdvIcon = advantage.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 * (index + 1) }}
                    className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-8 border border-[#8B5CF6]/30 hover:border-[#EC4899]/50 transition-all duration-300"
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${slide.gradient} flex items-center justify-center mb-6`}>
                      <AdvIcon size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">{advantage.title[language]}</h3>
                    <p className="text-lg text-[#8A90A0] leading-relaxed">{advantage.description[language]}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );

      case 'roadmap':
        return (
          <div className="max-w-7xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="space-y-6">
              {slide.phases.map((phase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 * (index + 1) }}
                  className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-2xl p-6 border border-[#1F2A3A] hover:border-opacity-50 transition-all duration-300"
                  style={{ borderColor: `${phase.color}40` }}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="md:w-48">
                      <div className="text-sm font-semibold mb-2" style={{ color: phase.color }}>
                        {phase.period}
                      </div>
                      <h3 className="text-2xl font-bold text-white">{phase.title[language]}</h3>
                    </div>
                    <div className="flex-1">
                      <ul className="space-y-3">
                        {phase.tasks.map((task, taskIndex) => (
                          <li key={taskIndex} className="flex items-start gap-3 text-[#8A90A0]">
                            <CheckCircle2 size={20} style={{ color: phase.color }} className="flex-shrink-0 mt-0.5" />
                            <span>{task[language]}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'business-model':
        return (
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-8">
              {slide.models.map((model, index) => {
                const ModelIcon = model.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 * (index + 1) }}
                    className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-8 border border-[#10B981]/30 hover:border-[#14B8A6]/50 transition-all duration-300 text-center"
                  >
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${slide.gradient} flex items-center justify-center mx-auto mb-6`}>
                      <ModelIcon size={40} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">{model.type[language]}</h3>
                    <p className="text-[#8A90A0] mb-6">{model.description[language]}</p>
                    <div className={`text-3xl font-bold bg-gradient-to-br ${slide.gradient} bg-clip-text text-transparent`}>
                      {model.pricing}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );

      case 'financials':
        return (
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold text-center mb-16 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {slide.projections.map((projection, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15 * (index + 1) }}
                  className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-8 border border-[#EF4444]/30 hover:border-[#F59E0B]/50 transition-all duration-300 text-center"
                >
                  <div className="text-5xl font-bold text-white mb-2">{projection.year}</div>
                  <div className={`text-4xl font-bold mb-4 bg-gradient-to-br ${slide.gradient} bg-clip-text text-transparent`}>
                    {projection.revenue}
                  </div>
                  <div className="text-lg text-[#10B981] font-semibold mb-3">{projection.customers}</div>
                  <div className="text-sm text-[#8A90A0]">{projection.status[language]}</div>
                </motion.div>
              ))}
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="max-w-6xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold mb-6 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-2xl text-white mb-8"
            >
              {slide.subtitle[language]}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-12 border border-[#7C3AED]/30 max-w-4xl mx-auto"
            >
              <Users size={80} className={`mx-auto mb-8 bg-gradient-to-br ${slide.gradient} bg-clip-text`} style={{ stroke: 'url(#gradient)' }} />
              <p className="text-xl text-[#8A90A0] leading-relaxed">
                {slide.description[language]}
              </p>
            </motion.div>
          </div>
        );

      case 'call-to-action':
        return (
          <div className="max-w-5xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-6xl font-bold mb-6 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`}
            >
              {slide.title[language]}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-3xl text-white mb-12"
            >
              {slide.subtitle[language]}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-[#1F2A3A]/40 backdrop-blur-sm rounded-3xl p-12 border border-[#14B8A6]/30 mb-8"
            >
              <div className={`text-5xl font-bold mb-8 bg-gradient-to-br ${slide.gradient} bg-clip-text text-transparent`}>
                {slide.content.seeking[language]}
              </div>
              <h3 className="text-2xl font-bold text-white mb-6">{slide.content.use[language]}</h3>
              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {slide.content.breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-[#0B1220]/50 rounded-xl p-4">
                    <span className="text-[#8A90A0]">{item.label[language]}</span>
                    <span className="text-2xl font-bold text-white">{item.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-xl text-[#8A90A0] mb-8"
            >
              {slide.contact[language]}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Button
                size="lg"
                className={`bg-gradient-to-r ${slide.gradient} text-white px-12 py-6 text-xl hover:scale-105 transition-transform`}
                onClick={() => window.location.href = '/contact'}
              >
                {language === 'en' ? 'Get in Touch' : language === 'nl' ? 'Neem Contact Op' : 'تواصل معنا'}
              </Button>
            </motion.div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0B1220]">
      <SEO
        title="YamAI Business & Innovation Plan"
        description="Comprehensive business plan for YamAI B.V. - Explainable AI for medical diagnostics"
        keywords="business plan, medical AI, healthcare innovation, investment opportunity"
      />

      <MorphingGradient />
      <AmbientBackground />

      {/* Main Slide Container */}
      <div className="relative z-10 h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
            className="w-full max-w-7xl"
          >
            {renderSlideContent(slides[currentSlide])}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Slide Navigation Dots */}
      <div className="fixed right-8 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'bg-gradient-to-r from-[#14B8A6] to-[#7C3AED] scale-125'
                : 'bg-[#8A90A0]/30 hover:bg-[#8A90A0]/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#1F2A3A]/80 backdrop-blur-sm rounded-full px-6 py-3 border border-[#7C3AED]/30">
        <span className="text-white font-semibold">
          {currentSlide + 1} / {totalSlides}
        </span>
      </div>

      {/* Navigation Arrows */}
      <div className="fixed bottom-8 right-8 z-50 flex gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => currentSlide > 0 && goToSlide(currentSlide - 1)}
          disabled={currentSlide === 0}
          className="bg-[#1F2A3A]/80 backdrop-blur-sm rounded-full w-12 h-12 hover:bg-[#7C3AED]/20 disabled:opacity-30"
        >
          <ChevronUp className="text-white" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => currentSlide < totalSlides - 1 && goToSlide(currentSlide + 1)}
          disabled={currentSlide === totalSlides - 1}
          className="bg-[#1F2A3A]/80 backdrop-blur-sm rounded-full w-12 h-12 hover:bg-[#7C3AED]/20 disabled:opacity-30"
        >
          <ChevronDown className="text-white" />
        </Button>
      </div>

      {/* SVG Gradients */}
      <svg width="0" height="0">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#14B8A6" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default BusinessPlan;
